import React, { useState } from "react";
import {
  Tooltip,
  IconButton,
  Collapse,
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";

import {
  ScrollWrapper,
  StyledTable,
  THead,
  TBody,
  Td,
  Tr,
  TableSkeleton,
  SortableTh,
} from "../../components/shared/TableComponents";
import { RatingStars } from "../../components/FieldResourceView/RatingStars";
import { EmptyState } from "../../components/shared/helpers/StatePrimitives";
import {
  DeleteButton,
  StyledTabButton,
} from "../../components/shared/helpers/GlobalStylesv5";
import { useTableSort } from "../../hooks/useTableSort";
import { tokens } from "../../theme/tokens";

// Thresholds are business rules and unchanged. Colour carries the two extremes;
// Fair and Not-rated are neutral, and every tier states its meaning in words
// (design doctrine §14 — colour is never the only signal).
function getRatingBadge(rating, ratings) {
  const r = Number(rating);
  if (!rating || r === 0 || Object.keys(ratings || {}).length === 0) {
    return { color: tokens.color.text.secondary, label: "Not rated" };
  }
  if (r < 1) return { color: tokens.color.status.error, label: "Poor" };
  if (r < 4) return { color: tokens.color.text.secondary, label: "Fair" };
  return { color: tokens.color.status.success, label: "Good" };
}

const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("") || "—";

const hasValue = (v) => v === 0 || (v != null && String(v).trim() !== "");
const money = (v) => (hasValue(v) ? `$${v}` : "—");

const RatingChip = ({ color, label }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 1,
      py: "2px",
      borderRadius: tokens.radius.full,
      backgroundColor: tokens.color.surface.sunken,
      fontSize: tokens.type.caption.size,
      fontWeight: 600,
      color,
      whiteSpace: "nowrap",
    }}
  >
    <Box
      component="span"
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        backgroundColor: "currentColor",
        flexShrink: 0,
      }}
    />
    {label}
  </Box>
);

// A titled group of detail rows. Borderless: the whole panel is one card, so a
// group being shorter or taller reads as a column of content, not a ragged box.
const GroupTitle = ({ children }) => (
  <Typography
    sx={{
      fontSize: "0.7rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: tokens.color.text.secondary,
      mb: 1.25,
    }}
  >
    {children}
  </Typography>
);

const DetailGroup = ({ title, children }) => (
  <Box>
    <GroupTitle>{title}</GroupTitle>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {children}
    </Box>
  </Box>
);

// One label → value line inside a DetailCard.
const Row = ({ label, children }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: 2,
    }}
  >
    <Typography
      sx={{
        fontSize: tokens.type.caption.size,
        color: tokens.color.text.secondary,
        flexShrink: 0,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: tokens.type.dense.size,
        fontWeight: 500,
        color: tokens.color.text.primary,
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {children}
    </Typography>
  </Box>
);

function CollapsibleRow({ row, setInitialValues, onDelete, mainColumns }) {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);
  const badge = getRatingBadge(row.average_rating, row.ratings);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleEditClick = () => {
    handleMenuClose();
    setInitialValues(row);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    onDelete(row.id);
  };

  return (
    <>
      <Tr>
        <Td width={mainColumns[0].width}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Td>
        <Td width={mainColumns[1].width}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                flex: "0 0 auto",
                borderRadius: tokens.radius.sm,
                backgroundColor: tokens.color.surface.sunken,
                color: tokens.color.brand.primary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
                fontSize: tokens.type.caption.size,
                textTransform: "uppercase",
              }}
            >
              {initials(row.name)}
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: tokens.color.text.primary }}
            >
              {row.name}
            </Typography>
          </Box>
        </Td>

        <Td width={mainColumns[2].width}>{row.email}</Td>
        <Td width={mainColumns[3].width}>{row.phone}</Td>
        <Td width={mainColumns[4].width}>{row.address}</Td>
        <Td width={mainColumns[5].width}>
          <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
            {row.type}
          </Typography>
        </Td>
        <Td width={mainColumns[6].width}>
          <Tooltip
            title={`Rating ${Number(row.average_rating).toFixed(2)}`}
            arrow
          >
            <span>
              <RatingChip color={badge.color} label={badge.label} />
            </span>
          </Tooltip>
        </Td>
        <Td width={mainColumns[7].width} align="center">
          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreVertIcon sx={{ width: 20, height: 20 }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                minWidth: 160,
                border: `1px solid ${tokens.color.border.default}`,
                borderRadius: tokens.radius.md,
                boxShadow: "none",
              },
            }}
          >
            <MenuItem onClick={handleEditClick}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDeleteClick}>
              <ListItemIcon>
                <DeleteIcon
                  fontSize="small"
                  sx={{ color: tokens.color.status.error }}
                />
              </ListItemIcon>
              <ListItemText
                sx={{
                  "& .MuiTypography-root": { color: tokens.color.status.error },
                }}
              >
                Delete
              </ListItemText>
            </MenuItem>
          </Menu>
        </Td>
      </Tr>
      <Tr>
        <Td style={{ padding: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                backgroundColor: tokens.color.surface.sunken,
                borderTop: `1px solid ${tokens.color.border.default}`,
                p: 2.5,
              }}
            >
              <Box
                sx={{
                  backgroundColor: tokens.color.surface.default,
                  border: `1px solid ${tokens.color.border.default}`,
                  borderRadius: tokens.radius.md,
                  p: 3,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 4,
                    alignItems: "start",
                  }}
                >
                  <DetailGroup title="Rates & rating">
                    {row.type === "processor" ? (
                      <Row label="Rate">{money(row.rate)}</Row>
                    ) : (
                      <>
                        <Row label="Hourly">{money(row.hourly_rate)}</Row>
                        <Row label="Mileage">{money(row.mileage_rate)}</Row>
                        <Row label="Database">{money(row.database_rate)}</Row>
                      </>
                    )}
                    <Row label="Rating">
                      <RatingStars value={row.average_rating} />
                    </Row>
                  </DetailGroup>

                  <DetailGroup title="Coverage">
                    <Row label="Radius">
                      {hasValue(row.radius_miles)
                        ? `${row.radius_miles} miles`
                        : "—"}
                    </Row>
                    <Row label="Coordinates">
                      {hasValue(row.lat) || hasValue(row.lng)
                        ? `${row.lat}, ${row.lng}`
                        : "—"}
                    </Row>
                  </DetailGroup>

                  <DetailGroup title="Company">
                    {row.meta?.company_name && (
                      <Row label="Company">{row.meta.company_name}</Row>
                    )}
                    {row.meta?.contact_name && (
                      <Row label="Contact">{row.meta.contact_name}</Row>
                    )}
                    {row.meta?.county && (
                      <Row label="County">{row.meta.county}</Row>
                    )}
                    {row.meta?.coverage_scope && (
                      <Row label="Coverage scope">
                        <span style={{ textTransform: "capitalize" }}>
                          {row.meta.coverage_scope}
                        </span>
                      </Row>
                    )}
                    {row.meta?.website && (
                      <Row label="Website">{row.meta.website}</Row>
                    )}
                    <Row label="W-9 on file">
                      {row.meta?.w9_on_file === "yes"
                        ? "Yes"
                        : row.meta?.w9_on_file === "no"
                          ? "No"
                          : "—"}
                    </Row>
                    {row.meta?.recommend && (
                      <Row label="Recommend">{row.meta.recommend}</Row>
                    )}
                  </DetailGroup>
                </Box>

                {row.meta?.notes && (
                  <Box
                    sx={{
                      mt: 3,
                      pt: 2.5,
                      borderTop: `1px solid ${tokens.color.border.default}`,
                    }}
                  >
                    <GroupTitle>Notes</GroupTitle>
                    <Typography
                      sx={{
                        fontSize: tokens.type.dense.size,
                        color: tokens.color.text.primary,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {row.meta.notes}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Collapse>
        </Td>
      </Tr>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <Typography variant="h5" style={{ padding: "16px" }}>
          Delete Field Resource
        </Typography>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{row.name}</strong>? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <StyledTabButton onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </StyledTabButton>
          <DeleteButton onClick={handleDeleteConfirm}>Delete</DeleteButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

function FieldResourceTable({ setInitialValues, onDelete, data, loading }) {
  const mainColumns = [
    { title: "", width: 32, sortKey: null },
    { title: "Name", width: 120, sortKey: "name", sortType: "string" },
    { title: "Email", width: 150, sortKey: "email", sortType: "string" },
    { title: "Phone", width: 100, sortKey: null },
    { title: "Address", width: 250, sortKey: "address", sortType: "string" },
    { title: "Type", width: 100, sortKey: "type", sortType: "string" },
    {
      title: "Rating",
      width: 90,
      sortKey: "average_rating",
      sortType: "number",
    },
    { title: "Options", width: 50, sortKey: null },
  ];

  const { sortedData, handleSort, getSortDirection } = useTableSort(
    data || [],
    mainColumns,
  );

  const cardSx = {
    backgroundColor: tokens.color.surface.default,
    border: `1px solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.md,
    overflow: "hidden",
  };

  if (!loading && (!data || data.length === 0)) {
    return (
      <Box sx={cardSx}>
        <EmptyState
          icon={<BadgeOutlinedIcon />}
          title="No field resources yet"
          description="Add a field resource with the Add Field Resource control above and it will appear here."
        />
      </Box>
    );
  }

  return (
    <Box sx={cardSx}>
      <ScrollWrapper>
        <StyledTable>
          <THead>
            <Tr>
              {mainColumns.map((col, idx) => (
                <SortableTh
                  key={col.title || idx}
                  width={col.width}
                  sortable={!!col.sortKey}
                  sortDirection={
                    col.sortKey ? getSortDirection(col.sortKey) : null
                  }
                  onSort={
                    col.sortKey ? () => handleSort(col.sortKey) : undefined
                  }
                >
                  {col.title}
                </SortableTh>
              ))}
            </Tr>
          </THead>

          <TBody>
            {loading ? (
              <TableSkeleton columns={8} mainColumns={mainColumns} rows={25} />
            ) : (
              sortedData.map((val, key) => (
                <CollapsibleRow
                  key={val.id || key}
                  row={val}
                  setInitialValues={setInitialValues}
                  onDelete={onDelete}
                  mainColumns={mainColumns}
                />
              ))
            )}
          </TBody>
        </StyledTable>
      </ScrollWrapper>
    </Box>
  );
}

export default FieldResourceTable;
